import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { X } from "@phosphor-icons/react";

const NumField = ({ label, value, onChange }) => (
  <div><label>{label}</label><input type="number" step="0.0001" value={value} onChange={(e) => onChange(e.target.value)} /></div>
);

export default function TaxConfigModal({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => { api.get("/tax/config").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return null;

  const setEii = (k, v) => setCfg((c) => ({ ...c, eii: { ...c.eii, [k]: v } }));
  const setSure = (k, v) => setCfg((c) => ({ ...c, sure: { ...c.sure, [k]: v } }));
  const setRd = (k, v) => setCfg((c) => ({ ...c, rd: { ...c.rd, [k]: v } }));

  const save = async () => {
    setMsg(null);
    try {
      const payload = {
        eii: {
          relief_rate: Number(cfg.eii.relief_rate),
          last_issue_date: cfg.eii.last_issue_date,
          annual_cap: Number(cfg.eii.annual_cap),
          lifetime_cap: Number(cfg.eii.lifetime_cap),
          minimum_holding_years: Number(cfg.eii.minimum_holding_years),
        },
        sure: {
          lookback_years: Number(cfg.sure.lookback_years),
          minimum_holding_years: Number(cfg.sure.minimum_holding_years),
        },
        rd: {
          uplift_rate: Number(cfg.rd.uplift_rate),
          credit_rate: Number(cfg.rd.credit_rate),
          corporation_tax_rate: Number(cfg.rd.corporation_tax_rate),
        },
        legislation_version: cfg.legislation_version,
      };
      await api.put("/tax/config", payload);
      setMsg({ ok: true, text: "Tax rules updated." });
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || "Save failed." });
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="tax-config-modal">
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="glass rounded-2xl w-full max-w-2xl my-8 p-8">
        <div className="flex items-center justify-between mb-6">
          <div><div className="eyebrow mb-1">Admin</div><div className="font-display text-2xl">Edit tax rules</div></div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="close-config"><X size={20} /></button>
        </div>

        <div className="space-y-6">
          <div>
            <div className="eyebrow mb-3">EII</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <NumField label="Relief rate (0-1)" value={cfg.eii.relief_rate} onChange={(v) => setEii("relief_rate", v)} />
              <div><label>Scheme end date</label><input type="date" value={cfg.eii.last_issue_date} onChange={(e) => setEii("last_issue_date", e.target.value)} /></div>
              <NumField label="Annual cap (€)" value={cfg.eii.annual_cap} onChange={(v) => setEii("annual_cap", v)} />
              <NumField label="Lifetime cap (€)" value={cfg.eii.lifetime_cap} onChange={(v) => setEii("lifetime_cap", v)} />
              <NumField label="Min holding years" value={cfg.eii.minimum_holding_years} onChange={(v) => setEii("minimum_holding_years", v)} />
            </div>
          </div>
          <div>
            <div className="eyebrow mb-3">SURE</div>
            <div className="grid grid-cols-2 gap-4">
              <NumField label="Lookback years" value={cfg.sure.lookback_years} onChange={(v) => setSure("lookback_years", v)} />
              <NumField label="Min holding years" value={cfg.sure.minimum_holding_years} onChange={(v) => setSure("minimum_holding_years", v)} />
            </div>
          </div>
          <div>
            <div className="eyebrow mb-3">R&D</div>
            <div className="grid grid-cols-3 gap-4">
              <NumField label="Uplift rate (0-1)" value={cfg.rd.uplift_rate} onChange={(v) => setRd("uplift_rate", v)} />
              <NumField label="Credit rate (0-1)" value={cfg.rd.credit_rate} onChange={(v) => setRd("credit_rate", v)} />
              <NumField label="CT rate (0-1)" value={cfg.rd.corporation_tax_rate} onChange={(v) => setRd("corporation_tax_rate", v)} />
            </div>
          </div>
          <div><label>Legislation version</label><input value={cfg.legislation_version} onChange={(e) => setCfg((c) => ({ ...c, legislation_version: e.target.value }))} /></div>
        </div>

        {msg ? <div className={`text-sm mt-4 ${msg.ok ? "text-[#00A86B]" : "text-[#E63946]"}`} data-testid="config-msg">{msg.text}</div> : null}
        <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-white/5">
          <button onClick={onClose} className="btn-ghost">Close</button>
          <button onClick={save} className="btn-gold" data-testid="save-config">Save rules</button>
        </div>
      </motion.div>
    </motion.div>
  );
}
